import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { api, useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Minimize2, Loader2,
  ExternalLink, Sparkles, Download, Image as ImageIcon, Clipboard,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { getISOWeekNow, columnHeaderLabel, dayShortI18n, monthShortI18n } from './planning/utils';
import { useTranslation } from '../lib/i18n';

/**
 * PlanningBoardCanvas — read-only Miro-style infinite canvas of all weeks in
 * a year. Each week renders as a card containing one mini-table per firm
 * with rows=days, cols=routes. Cells show:
 *   • assigned employee_name (compact) OR
 *   • "DEPO" placeholder OR
 *   • blank (empty/closed)
 *
 * Interactions:
 *  • Mouse wheel  → zoom (centred on cursor)
 *  • Drag canvas  → pan
 *  • Click "Aç"   → jump to /dashboard/planning?year&week (full editor)
 *
 * Sources data from /planning/yearly-summary + /planning/assignments-list +
 * /planning/routes — all already exist. No backend changes needed.
 */
const FIRM_PALETTE = [
  { bar: 'bg-orange-500', text: 'text-white' },
  { bar: 'bg-blue-700',   text: 'text-white' },
  { bar: 'bg-emerald-700',text: 'text-white' },
  { bar: 'bg-red-600',    text: 'text-white' },
  { bar: 'bg-violet-600', text: 'text-white' },
  { bar: 'bg-slate-900',  text: 'text-white' },
  { bar: 'bg-pink-600',   text: 'text-white' },
];
function firmStyle(name) {
  if (!name) return FIRM_PALETTE[5];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return FIRM_PALETTE[h % FIRM_PALETTE.length];
}

const DAY_LABELS_SHORT_FALLBACK = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

// Bir firma altında gösterilebilecek maksimum rota sayısı. HYK gibi büyük
// operatörlerde 15-20 rota olabiliyor; 8'lik eski sınır onları gizliyordu.
// Card genişliği bunu karşılayacak şekilde 540 → 820'ye çıkarıldı.
const ROUTES_PER_FIRM = 20;

const CARD_W = 820;
const CARD_H = 560;
const COL_GAP = 80;
const ROW_GAP = 80;
const CARDS_PER_ROW = 4; // 4 × ~14 rows ≈ 54 weeks (kart genişliği arttı)

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Trigger a styled-XLSX download for a single week. Uses axios+blob so the
 * Authorization header travels with the request (a bare <a> can't carry it).
 * `t` is the i18n resolver; messages stay locale-aware.
 */
async function downloadStyledWeek(year, week, t) {
  try {
    const token = useAuthStore.getState().accessToken;
    const res = await axios.get(`${API_URL}/api/planning/export-week-styled?year=${year}&week=${week}`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `planlama_W${String(week).padStart(2, '0')}_${year}_renkli.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(t('planning.boardCanvas.excelDownloaded', { week }));
  } catch (err) {
    toast.error(err.response?.data?.detail || t('planning.boardCanvas.downloadFailed'));
  }
}

export default function PlanningBoardCanvas() {
  const { t } = useTranslation();
  const _now = useMemo(getISOWeekNow, []);
  const [year, setYear] = useState(_now.year);
  const [summary, setSummary] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pan/zoom state
  const [scale, setScale] = useState(0.4);
  const [pan, setPan] = useState({ x: 60, y: 60 });
  // Smooth-zoom flag — buton/zoom-fit aksiyonlarında 250ms CSS transition
  // uygular. Drag/pinch sırasında KAPATILIR (60Hz frame'lerde transition
  // jank yaratır). useRef + state karması: state inline style'ı tetiklemek
  // için, timer ref ise üst üste binen animasyonların eski timeout'unu
  // iptal etmek için.
  const [animating, setAnimating] = useState(false);
  const animTimerRef = useRef(null);
  const animateNext = () => {
    setAnimating(true);
    if (animTimerRef.current) clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => setAnimating(false), 280);
  };
  const dragRef = useRef(null);
  // Mobile pinch-zoom + 1-finger pan refs. We don't put these in React state
  // because gesture deltas fire 60+ Hz — re-rendering on each pointer move
  // would jank. Only the FINAL `pan`/`scale` is in state (set inside the
  // raf-throttled handlers below).
  const touchRef = useRef(null); // { mode: 'pan' | 'pinch', ... }
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, a, r] = await Promise.all([
          api.get(`/planning/yearly-summary?year=${year}`),
          api.get(`/planning/assignments-list?year=${year}`),
          api.get('/planning/routes'),
        ]);
        if (cancelled) return;
        setSummary(s.data?.data || null);
        setAssignments(a.data?.data || []);
        setRoutes(r.data?.data || []);
      } catch {
        if (!cancelled) toast.error(t('planning.boardCanvas.dataLoadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  // Group assignments per (week, firm) → { firmName: { dayIdx: { route_id: assignment } } }
  const weekData = useMemo(() => {
    const m = new Map();
    for (const a of assignments) {
      const wk = a.week_number;
      const firm = a.firm_name || a.owner_firm_name || 'Diğer';
      if (!m.has(wk)) m.set(wk, new Map());
      const firms = m.get(wk);
      if (!firms.has(firm)) firms.set(firm, { byDay: new Map(), routeIds: new Set() });
      const grp = firms.get(firm);
      const dt = new Date(a.assignment_date);
      const dayIdx = (dt.getDay() + 6) % 7;
      grp.routeIds.add(a.route_id || a.route_code);
      if (!grp.byDay.has(dayIdx)) grp.byDay.set(dayIdx, new Map());
      grp.byDay.get(dayIdx).set(a.route_id || a.route_code, a);
    }
    return m;
  }, [assignments]);

  // ── Pan handlers (left-mouse drag on empty canvas) ──────────────────────
  const onMouseDown = (e) => {
    // Only start panning when not clicking inside a card link
    if (e.target.closest('a, button')) return;
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.x),
      y: dragRef.current.panY + (e.clientY - dragRef.current.y),
    });
  };
  const onMouseUp = () => { dragRef.current = null; };

  // Masaüstünde de çift tıklama ile kart-zoom çalışsın. React'in
  // synthetic `onDoubleClick` bazı setup'larda untrusted/programmatic
  // event'leri yakalamayabildiği için, kendi click-counter'ımızla
  // manuel double-tap algılaması yapıyoruz (300ms eşik, 30px tolerans).
  const lastClickRef = useRef(null);
  const onClickDetect = (e) => {
    if (e.target.closest('a, button')) return;
    const cardEl = e.target.closest('[data-week-idx]');
    if (!cardEl) return;
    const idx = parseInt(cardEl.getAttribute('data-week-idx'), 10);
    if (!Number.isFinite(idx) || idx < 0) return;
    const now = Date.now();
    const last = lastClickRef.current;
    if (last && now - last.t < 300 && Math.hypot(e.clientX - last.x, e.clientY - last.y) < 30 && idx === last.idx) {
      zoomToCard(idx);
      lastClickRef.current = null;
      return;
    }
    lastClickRef.current = { t: now, x: e.clientX, y: e.clientY, idx };
  };

  // Zoom with mouse-wheel — anchor at cursor.
  const onWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const next = Math.max(0.15, Math.min(2.5, scale * factor));
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) { setScale(next); return; }
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    setPan((p) => ({
      x: cx - ((cx - p.x) * next) / scale,
      y: cy - ((cy - p.y) * next) / scale,
    }));
    setScale(next);
  };

  const fitAll = () => { animateNext(); setScale(0.25); setPan({ x: 40, y: 40 }); };
  const zoomIn = () => { animateNext(); setScale((s) => Math.min(2.5, s * 1.25)); };
  const zoomOut = () => { animateNext(); setScale((s) => Math.max(0.15, s / 1.25)); };

  // Çift dokunma → tıklanan kartı ekrana sığdır. Kart `data-week-idx`
  // attribute'unu taşır; world coords idx'ten deterministik çıkar.
  // Hedef scale: kartı padding ile sığdıran maks. değer (her iki eksende),
  // [0.15, 2.5] aralığına clamp. Pan kartı viewport ortasına anchor eder.
  const lastTapRef = useRef(null); // { t: timestamp, x, y, idx }
  const zoomToCard = (idx) => {
    if (idx == null || idx < 0) return;
    const col = idx % CARDS_PER_ROW;
    const row = Math.floor(idx / CARDS_PER_ROW);
    const cardX = COL_GAP + col * (CARD_W + COL_GAP);
    const cardY = ROW_GAP + row * (CARD_H + ROW_GAP);
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const padding = 40;
    const targetScale = Math.max(0.15, Math.min(2.5,
      Math.min((rect.width - padding * 2) / CARD_W, (rect.height - padding * 2) / CARD_H)
    ));
    animateNext();
    setScale(targetScale);
    setPan({
      x: rect.width / 2 - (cardX + CARD_W / 2) * targetScale,
      y: rect.height / 2 - (cardY + CARD_H / 2) * targetScale,
    });
  };

  // ── Touch handlers (mobile) ─────────────────────────────────────────────
  // 1 finger  → pan (aynı mouse drag mantığı)
  // 2 finger  → pinch-zoom (anchor: iki parmak orta noktası — mouse-wheel
  //             cursor-anchor zoom ile aynı matematik)
  // Ek: parmaklar kart üzerine bassa bile pan/pinch yine çalışsın diye
  // root <div>'a `touchAction: 'none'` veriyoruz ve elemana iniyor — her
  // dokunuş event listener'a iletiliyor.
  const distance = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
  const midpoint = (t1, t2) => ({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });

  const onTouchStart = (e) => {
    if (e.target.closest('a, button')) return; // tıklanabilir öğelere dokunmayı bozma
    if (e.touches.length === 1) {
      const t = e.touches[0];
      // Çift dokunma algılama: 300ms içinde ~30px yarıçapta aynı kart üzerinde
      // ikinci touchstart → o kartı ekrana sığdır.
      const cardEl = e.target.closest('[data-week-idx]');
      const idx = cardEl ? parseInt(cardEl.getAttribute('data-week-idx'), 10) : -1;
      const now = Date.now();
      const last = lastTapRef.current;
      if (last && now - last.t < 300 && Math.hypot(t.clientX - last.x, t.clientY - last.y) < 30 && idx === last.idx && idx >= 0) {
        zoomToCard(idx);
        lastTapRef.current = null;
        touchRef.current = null;
        return;
      }
      lastTapRef.current = { t: now, x: t.clientX, y: t.clientY, idx };
      touchRef.current = { mode: 'pan', x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y };
    } else if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      touchRef.current = {
        mode: 'pinch',
        startDist: distance(a, b),
        startScale: scale,
        startPan: { ...pan },
        startMid: midpoint(a, b),
      };
      lastTapRef.current = null; // pinch başladıysa double-tap iptal
    }
  };

  const onTouchMove = (e) => {
    if (!touchRef.current) return;
    e.preventDefault(); // browser'ın scroll/refresh davranışını engelle
    if (touchRef.current.mode === 'pan' && e.touches.length === 1) {
      const t = e.touches[0];
      setPan({
        x: touchRef.current.panX + (t.clientX - touchRef.current.x),
        y: touchRef.current.panY + (t.clientY - touchRef.current.y),
      });
    } else if (touchRef.current.mode === 'pinch' && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = distance(a, b);
      if (dist <= 0) return;
      const ratio = dist / touchRef.current.startDist;
      const next = Math.max(0.15, Math.min(2.5, touchRef.current.startScale * ratio));
      // Pinch midpoint relative to the wrapper (canvas origin).
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) { setScale(next); return; }
      const cx = touchRef.current.startMid.x - rect.left;
      const cy = touchRef.current.startMid.y - rect.top;
      const sp = touchRef.current.startPan;
      // Same anchor math as mouse-wheel zoom but using the START scale/pan
      // as the reference frame so the pinch feels stable (no compounding
      // drift between successive frames).
      const k = next / touchRef.current.startScale;
      setPan({
        x: cx - (cx - sp.x) * k,
        y: cy - (cy - sp.y) * k,
      });
      setScale(next);
    }
  };

  const onTouchEnd = (e) => {
    // 2 → 1 parmağa düştüğünde pan moduna geçiş (kullanıcı pinch'ten sonra
    // bir parmağı bırakıp diğerini sürüklemek isterse).
    if (e.touches.length === 1 && touchRef.current?.mode === 'pinch') {
      const t = e.touches[0];
      touchRef.current = { mode: 'pan', x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y };
    } else if (e.touches.length === 0) {
      touchRef.current = null;
    }
  };

  // React'in synthetic `onTouchMove` event'i root'a passive olarak bağlanır
  // → `preventDefault()` no-op olur. Dolayısıyla touchmove'u native
  // listener olarak `{ passive: false }` opsiyonuyla bağlamamız gerekiyor;
  // aksi halde mobil tarayıcı sayfayı scroll/zoom eder ve canvas içindeki
  // pinch-zoom çalışmaz.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const handler = (e) => onTouchMove(e);
    el.addEventListener('touchmove', handler, { passive: false });
    return () => el.removeEventListener('touchmove', handler);
    // onTouchMove kapanışı pan/scale'i okuduğu için her render'da yeniden
    // bağlamak istiyoruz — performans riski yok, listener tek seferlik.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const totalRows = Math.ceil(53 / CARDS_PER_ROW);
  const worldW = CARDS_PER_ROW * (CARD_W + COL_GAP) + COL_GAP;
  const worldH = totalRows * (CARD_H + ROW_GAP) + ROW_GAP;

  return (
    <div
      ref={wrapRef}
      className="fixed inset-0 top-16 left-0 md:left-[72px] bg-[radial-gradient(ellipse_at_center,#0f172a_0%,#020617_100%)] overflow-hidden select-none cursor-grab active:cursor-grabbing"
      style={{ touchAction: 'none' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onClick={onClickDetect}
      onWheel={onWheel}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      data-testid="planning-canvas"
    >
      {/* ── Top toolbar ─────────────────────────────────────────────── */}
      <div className="absolute top-3 left-3 right-3 z-20 flex flex-wrap items-center gap-2 pointer-events-none">
        <div className="pointer-events-auto inline-flex items-center gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-xl bg-slate-900/80 border border-slate-700/60 backdrop-blur shadow-lg">
          <div className="h-6 w-6 md:h-7 md:w-7 rounded-md bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 md:h-4 md:w-4 text-slate-900" />
          </div>
          <span className="text-xs md:text-sm font-bold tracking-tight text-emerald-300">Fleet</span>
          <span className="text-xs md:text-sm font-medium text-slate-200 hidden sm:inline">{t('planning.boardCanvas.title')}</span>
        </div>
        <div className="pointer-events-auto inline-flex items-center gap-1 px-1.5 py-1 rounded-xl bg-slate-900/80 border border-slate-700/60 backdrop-blur">
          <button onClick={() => setYear(year - 1)} className="p-1.5 rounded hover:bg-slate-800 text-slate-300" data-testid="canvas-prev-year"><ChevronLeft className="h-4 w-4" /></button>
          <span className="text-sm font-bold tabular-nums px-2 text-emerald-300">{year}</span>
          <button onClick={() => setYear(year + 1)} className="p-1.5 rounded hover:bg-slate-800 text-slate-300" data-testid="canvas-next-year"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="pointer-events-auto md:ml-auto flex items-center gap-2">
          <div className="hidden md:block"><Legend t={t} /></div>
          <div className="inline-flex items-center gap-1 px-1.5 py-1 rounded-xl bg-slate-900/80 border border-slate-700/60 backdrop-blur">
            <button onClick={zoomOut} className="p-1.5 rounded hover:bg-slate-800 text-slate-300" title={t('planning.boardCanvas.zoomOut')} data-testid="canvas-zoom-out"><ZoomOut className="h-4 w-4" /></button>
            <span className="text-[10px] tabular-nums px-1 text-slate-400 w-10 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} className="p-1.5 rounded hover:bg-slate-800 text-slate-300" title={t('planning.boardCanvas.zoomIn')} data-testid="canvas-zoom-in"><ZoomIn className="h-4 w-4" /></button>
            <button onClick={fitAll} className="p-1.5 rounded hover:bg-slate-800 text-slate-300" title={t('planning.boardCanvas.fitAll')} data-testid="canvas-fit"><Maximize2 className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      {/* Mobile-only bottom Legend — collapsible pill anchored to the bottom
          center so it doesn't eat horizontal space. Desktop shows the legend
          in the top toolbar (hidden here by md:hidden). */}
      <MobileLegend t={t} />

      {loading && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/80 border border-slate-700/50 backdrop-blur text-xs text-slate-300">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('planning.boardCanvas.preparing')}
        </div>
      )}

      {/* ── Infinite canvas (transformed) ────────────────────────────── */}
      <div
        className="absolute origin-top-left"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: '0 0',
          width: worldW,
          height: worldH,
          transition: animating ? 'transform 250ms ease-out' : 'none',
        }}
      >
        {(summary?.weeks || []).map((w, idx) => {
          const col = idx % CARDS_PER_ROW;
          const row = Math.floor(idx / CARDS_PER_ROW);
          const x = COL_GAP + col * (CARD_W + COL_GAP);
          const y = ROW_GAP + row * (CARD_H + ROW_GAP);
          return (
            <WeekCard
              key={w.week}
              x={x}
              y={y}
              idx={idx}
              week={w}
              firms={weekData.get(w.week)}
              t={t}
            />
          );
        })}
      </div>
    </div>
  );
}

function Legend({ t }) {
  const items = [
    [t('planning.boardCanvas.legend.depot'), 'bg-slate-800 border-slate-600 text-slate-200'],
    [t('planning.boardCanvas.legend.assignment'), 'bg-emerald-500 border-emerald-400 text-white'],
    [t('planning.boardCanvas.legend.driver'), 'bg-blue-500 border-blue-400 text-white'],
    [t('planning.boardCanvas.legend.day'), 'bg-emerald-700 border-emerald-600 text-white'],
  ];
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-900/80 border border-slate-700/60 backdrop-blur text-[11px] text-slate-300">
      {items.map(([label, cls]) => (
        <span key={label} className="inline-flex items-center gap-1">
          <span className={`inline-block h-3 w-3 rounded-sm border ${cls}`} />
          {label}
        </span>
      ))}
    </div>
  );
}

/** Mobile-only Legend pill anchored to the bottom of the canvas. Collapsible
 *  so it doesn't permanently block the view; tap the "i" chip to toggle. */
function MobileLegend({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-20 pointer-events-auto">
      {open ? (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/90 border border-slate-700/60 backdrop-blur shadow-lg text-[10px] text-slate-200">
          <button
            onClick={() => setOpen(false)}
            className="p-0.5 rounded hover:bg-slate-800 text-slate-400"
            aria-label="close legend"
            data-testid="canvas-mobile-legend-close"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <Legend t={t} />
        </div>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-900/90 border border-slate-700/60 backdrop-blur shadow-lg text-[11px] font-semibold text-emerald-300 hover:bg-slate-800"
          data-testid="canvas-mobile-legend-toggle"
        >
          <Sparkles className="h-3 w-3" />
          {t('planning.boardCanvas.legendToggle') || 'Açıklama'}
        </button>
      )}
    </div>
  );
}

function WeekCard({ x, y, week, firms, t, idx }) {
  const start = new Date(week.start_date);
  const end = new Date(week.end_date);
  // `16 Nis Pzt` formatında, dile göre dinamik. Monday-indexed helper:
  //   start → dayIdx 0 (Mon), end → dayIdx 6 (Sun) by ISO week convention.
  const fmt = (d, dayIdxMondayFirst) =>
    `${d.getDate()} ${monthShortI18n(t, d.getMonth())} ${dayShortI18n(t, dayIdxMondayFirst)}`;
  const firmList = useMemo(() => (firms ? [...firms.entries()].sort((a, b) => a[0].localeCompare(b[0], 'tr')) : []), [firms]);
  // Per-kart genişletme — 3+ firma veya 8+ rota olduğunda kart varsayılan
  // boyutuna sığmıyor. Kullanıcı "büyüt" düğmesiyle tüm firmaları ve tüm
  // rotaları (slice yok) görebilir; tıklanan kart üst/sağ komşuların üstüne
  // geçici olarak taşar (z-30) ki tam görüntü temiz kalsın.
  const [expanded, setExpanded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);
  const visibleFirms = expanded ? firmList : firmList.slice(0, 4);

  // PNG / Clipboard dışa aktarımı — kartı yakalamadan önce expanded'a alıp
  // kısa bir tick bekliyoruz ki tüm firma/rota tabloları DOM'a girsin.
  // Aksi halde sadece header gradient'i ve overflow-hidden body kapsanıyor,
  // resmin içi boş çıkıyor (kullanıcının raporladığı bug).
  // html-to-image transform'lu (pan/zoom) bir parent'ta gömülü olduğunda
  // bounding box bozulduğu için açıkça `width` + `style.position: 'static'`
  // veriyoruz; capture sonrası eski expanded haline geri dönüyoruz.
  // Map firmStyle bar class → solid hex (yalnızca FIRMA başlık çubuğu için).
  // Hücreler ekrandaki canlı görünümle aynı kalır: mavi atama hücresi, koyu
  // arka plan, emerald gün etiketi. Operatör PNG'yi gördüğünde "ekranda nasıl
  // duruyorsa öyle" deneyimini alır.
  const TONE = {
    'bg-orange-500':  '#f97316',
    'bg-blue-700':    '#1d4ed8',
    'bg-emerald-700': '#047857',
    'bg-red-600':     '#dc2626',
    'bg-violet-600':  '#7c3aed',
    'bg-slate-900':   '#0f172a',
    'bg-pink-600':    '#db2777',
  };

  const buildExportNode = () => {
    const node = document.createElement('div');
    node.style.cssText = `
      position: fixed; left: -10000px; top: 0;
      width: 1100px; padding: 24px;
      background: #0f172a; color: #e5e7eb;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-sizing: border-box;
    `;
    const start = new Date(week.start_date);
    const end = new Date(week.end_date);
    const fmt = (d, dayIdxMondayFirst) =>
      `${d.getDate()} ${monthShortI18n(t, d.getMonth())} ${dayShortI18n(t, dayIdxMondayFirst)}`;
    const dayLabels = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

    // Header — canlı UI'daki "W18 · 27 Nis Pzt → 3 May Paz · 14 atama" hizası
    const header = document.createElement('div');
    header.style.cssText = 'display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #334155;';
    header.innerHTML = `
      <span style="font-size: 36px; font-weight: 900; color: #6ee7b7; letter-spacing: -1px;">W${week.week}</span>
      <span style="font-size: 13px; color: #94a3b8;">${fmt(start, 0)} → ${fmt(end, 6)}</span>
      <span style="margin-left: auto; padding: 4px 10px; background: rgba(16, 185, 129, 0.18); border-radius: 4px; font-size: 11px; color: #6ee7b7; font-weight: 700;">
        ${week.assigned} atama
      </span>
    `;
    node.appendChild(header);

    // Per-firm tables — header bar firma renginde, hücreler ekrandaki canlı
    // görünümle birebir mavi (atama) / koyu (boş).
    for (const [firmName, grp] of firmList) {
      const style = firmStyle(firmName);
      const firmHex = TONE[style.bar] || '#475569';
      const routeIds = [...grp.routeIds];
      const routeCode = new Map();
      for (const dayMap of grp.byDay.values()) {
        for (const [rid, a] of dayMap) if (!routeCode.has(rid)) routeCode.set(rid, a.route_code);
      }
      // Firm header bar — canlı UI ile aynı (orange/red/green/blue).
      const firmBar = document.createElement('div');
      firmBar.style.cssText = `background: ${firmHex}; padding: 8px 14px; font-weight: 800; font-size: 14px; color: white; border-radius: 6px 6px 0 0; margin-top: 10px; text-shadow: 0 1px 2px rgba(0,0,0,0.4);`;
      firmBar.textContent = firmName;
      node.appendChild(firmBar);

      const table = document.createElement('table');
      table.style.cssText = 'width: 100%; border-collapse: collapse; font-size: 12px; background: #1e293b; border-radius: 0 0 6px 6px; overflow: hidden;';

      // Header row — emerald "Gün" + slate route codes (ekrandaki gibi)
      const thead = document.createElement('thead');
      const headRow = document.createElement('tr');
      const dayTh = document.createElement('th');
      dayTh.style.cssText = 'padding: 6px 8px; text-align: left; font-size: 11px; color: #6ee7b7; background: rgba(16, 185, 129, 0.15); width: 50px;';
      dayTh.textContent = 'Gün';
      headRow.appendChild(dayTh);
      for (const rid of routeIds) {
        const th = document.createElement('th');
        th.style.cssText = 'padding: 6px 8px; text-align: center; font-size: 10px; color: #94a3b8; font-family: monospace; font-weight: 700; min-width: 70px; background: #1e293b;';
        th.textContent = routeCode.get(rid) || '—';
        headRow.appendChild(th);
      }
      thead.appendChild(headRow);
      table.appendChild(thead);

      // Body — Pzt..Cmt (Pazar gizli). Day label emerald, atama hücreleri
      // ekrandaki gibi MAVİ (bg-blue-600/80 → #2563eb), boş hücreler koyu.
      const tbody = document.createElement('tbody');
      for (let di = 0; di < 6; di += 1) {
        const tr = document.createElement('tr');
        tr.style.cssText = 'border-top: 1px solid rgba(51, 65, 85, 0.5);';
        const dayTd = document.createElement('td');
        dayTd.style.cssText = 'padding: 4px 8px; font-weight: 700; color: #6ee7b7; background: rgba(16, 185, 129, 0.25); font-size: 11px;';
        dayTd.textContent = dayLabels[di];
        tr.appendChild(dayTd);
        const dayMap = grp.byDay.get(di);
        for (const rid of routeIds) {
          const td = document.createElement('td');
          const a = dayMap?.get(rid);
          if (!a) {
            // Boş hücre — slate-900/40 (ekrandaki bg-slate-900/40)
            td.style.cssText = 'padding: 4px 6px; background: rgba(15, 23, 42, 0.5); text-align: center;';
            td.innerHTML = '&nbsp;';
          } else if (a.status === 'depot') {
            td.style.cssText = 'padding: 4px 6px; background: #334155; color: #cbd5e1; text-align: center; font-weight: 700; font-size: 10px;';
            td.textContent = t('planning.boardCanvas.depot');
          } else {
            const initials = (a.employee_name || '').split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase();
            const plate = (a.vehicle_code || '').trim();
            // Atama hücresi — bg-blue-600/80 ile aynı mavi ton, baş harfler
            // beyaz, plaka amber-200 (ekrandaki gibi).
            td.style.cssText = 'padding: 3px 6px; background: rgba(37, 99, 235, 0.85); color: white; text-align: center; font-weight: 700; line-height: 1.15; font-size: 11px;';
            td.innerHTML = `
              <div>${initials || '·'}</div>
              ${plate ? `<div style="font-size: 9px; font-family: monospace; color: #fde68a; font-weight: 600;">${plate}</div>` : ''}
            `;
          }
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }
      table.appendChild(tbody);
      node.appendChild(table);
    }

    document.body.appendChild(node);
    return node;
  };

  const captureCard = async () => {
    const node = buildExportNode();
    try {
      // Layout settle — fontlar için kısa bir tick.
      await new Promise((r) => setTimeout(r, 50));
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: '#0f172a',
        useCORS: true,
        logging: false,
      });
      return canvas.toDataURL('image/png');
    } finally {
      document.body.removeChild(node);
    }
  };

  const handleExportPng = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const dataUrl = await captureCard();
      if (!dataUrl) throw new Error('No data');
      const link = document.createElement('a');
      link.download = `Plan_${week.year}_W${String(week.week).padStart(2, '0')}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('PNG indirildi');
    } catch (err) {
      console.error('PNG export error:', err);
      toast.error('PNG oluşturulamadı');
    } finally {
      setExporting(false);
    }
  };

  // 📋 Panoya kopyala — operatör WhatsApp/Telegram/E-posta'ya doğrudan
  // yapıştırabilir. Clipboard API yalnızca PNG blob'u kabul eder; jpeg
  // veya başka MIME desteklenmez. https permissions-policy gerektiriyor.
  const handleCopyPng = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      if (!navigator.clipboard || !window.ClipboardItem) {
        toast.error('Tarayıcınız panoya görsel kopyalamayı desteklemiyor');
        return;
      }
      const dataUrl = await captureCard();
      if (!dataUrl) throw new Error('No data');
      const blob = await (await fetch(dataUrl)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      toast.success('Panoya kopyalandı — yapıştırabilirsiniz');
    } catch (err) {
      console.error('Clipboard copy error:', err);
      toast.error('Panoya kopyalanamadı');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div
      ref={cardRef}
      className={`absolute rounded-2xl bg-slate-900/70 border border-slate-700/50 backdrop-blur shadow-2xl shadow-black/40 ${expanded ? 'z-30' : 'overflow-hidden'}`}
      style={{
        left: x,
        top: y,
        width: expanded ? CARD_W + 60 : CARD_W,
        height: expanded ? 'auto' : CARD_H,
        minHeight: CARD_H,
      }}
      data-testid={`canvas-week-${week.week}`}
      data-week-idx={idx}
    >
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-slate-800/80 to-slate-900/40 border-b border-slate-700/50">
        <span className="text-2xl font-black text-emerald-300 tabular-nums">W{week.week}</span>
        <span className="text-[11px] text-slate-400">{fmt(start, 0)} → {fmt(end, 6)}</span>
        <span className="ml-auto inline-flex items-center gap-2 text-[10px] text-slate-500">
          {week.assigned > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-bold">{t('planning.boardCanvas.assignmentsCount', { count: week.assigned })}</span>
          )}
          {week.assigned > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); downloadStyledWeek(week.year, week.week, t); }}
              onMouseDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-0.5 text-amber-400 hover:text-amber-200 hover:underline font-semibold"
              title={t('planning.boardCanvas.downloadExcelTitle')}
              data-testid={`canvas-download-${week.week}`}
            >
              <Download className="h-3 w-3" /> XLSX
            </button>
          )}
          {/* PNG screenshot — XLSX yanında, aynı renkli görünümü resim olarak
              indirir. html-to-image kütüphanesi mevcut DOM'u rasterize eder
              (Tailwind class'ları, gradient, badge'ler korunur). */}
          {week.assigned > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleExportPng(); }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={exporting}
              className="inline-flex items-center gap-0.5 text-fuchsia-400 hover:text-fuchsia-200 hover:underline font-semibold disabled:opacity-50"
              title="PNG olarak indir"
              data-testid={`canvas-png-${week.week}`}
            >
              {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <ImageIcon className="h-3 w-3" />}
              PNG
            </button>
          )}
          {/* 📋 Panoya kopyala — Clipboard API ile PNG blob'u panoya yazılır.
              Operatör WhatsApp / E-posta / Telegram'da Ctrl+V ile yapıştırır. */}
          {week.assigned > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); handleCopyPng(); }}
              onMouseDown={(e) => e.stopPropagation()}
              disabled={exporting}
              className="inline-flex items-center gap-0.5 text-cyan-400 hover:text-cyan-200 hover:underline font-semibold disabled:opacity-50"
              title="Panoya kopyala (WhatsApp/E-posta'ya yapıştırın)"
              data-testid={`canvas-copy-${week.week}`}
            >
              {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Clipboard className="h-3 w-3" />}
              Kopyala
            </button>
          )}
          {/* Büyüt / küçült — bir hafta içinde sığmayan firma/rota sayısı varsa
              kullanıcı tıklayarak tam içeriği açabilir. Pan/drag tetiklenmesin
              diye onMouseDown stopPropagation. */}
          {firmList.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              onMouseDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-0.5 text-sky-400 hover:text-sky-200 hover:underline font-semibold"
              title={expanded ? 'Küçült' : 'Büyüt — tüm firmaları/rotaları göster'}
              data-testid={`canvas-expand-${week.week}`}
            >
              {expanded ? <Minimize2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
              {expanded ? 'Küçült' : 'Büyüt'}
            </button>
          )}
          <Link
            to={`/dashboard/planning?year=${week.year}&week=${week.week}`}
            className="inline-flex items-center gap-0.5 text-emerald-400 hover:text-emerald-200 hover:underline font-semibold"
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={`canvas-open-${week.week}`}
          >
            {t('planning.boardCanvas.open')} <ExternalLink className="h-3 w-3" />
          </Link>
        </span>
      </div>

      <div className={`p-3 space-y-2 ${expanded ? 'overflow-visible' : 'overflow-hidden'}`} style={expanded ? {} : { height: CARD_H - 60 }}>
        {firmList.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-600 text-xs italic">
            <span>{t('planning.boardCanvas.noAssignmentsWeek')}</span>
          </div>
        ) : (
          visibleFirms.map(([firmName, grp]) => (
            <FirmTable key={firmName} firmName={firmName} grp={grp} t={t} expanded={expanded} />
          ))
        )}
        {!expanded && firmList.length > 4 && (
          <div className="text-[10px] text-slate-500 italic text-center pt-1">
            {t('planning.boardCanvas.moreFirms', { count: firmList.length - 4 })}
          </div>
        )}
      </div>
    </div>
  );
}

function FirmTable({ firmName, grp, t, expanded = false }) {
  const style = firmStyle(firmName);
  const routeIds = useMemo(() => [...grp.routeIds], [grp.routeIds]);
  // First-occurrence route_code lookup
  const routeCode = useMemo(() => {
    const m = new Map();
    for (const dayMap of grp.byDay.values()) {
      for (const [rid, a] of dayMap) if (!m.has(rid)) m.set(rid, a.route_code);
    }
    return m;
  }, [grp]);
  const days = [0, 1, 2, 3, 4, 5]; // Mon-Sat (Sunday hidden in compact view)
  // Genişletilmiş kartta tüm rotaları göster (slice yok); küçük kartta
  // ROUTES_PER_FIRM (20) ile sınırla.
  const visibleRouteIds = expanded ? routeIds : routeIds.slice(0, ROUTES_PER_FIRM);

  return (
    <div className="rounded-md overflow-hidden border border-slate-700/60 bg-slate-950/40">
      <div className={`${style.bar} ${style.text} text-[11px] font-bold px-2 py-1 truncate`}>
        {firmName}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px] text-slate-300 border-collapse">
          <thead>
            <tr>
              <th className="px-1.5 py-0.5 text-left text-emerald-300 font-bold w-10 sticky left-0 bg-slate-950/40 z-10">{t('planning.boardCanvas.dayCol')}</th>
              {visibleRouteIds.map((rid) => (
                <th key={rid} className="px-1 py-0.5 text-center text-slate-400 font-mono font-bold whitespace-nowrap">{routeCode.get(rid) || '—'}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {days.map((di) => {
              const dayMap = grp.byDay.get(di);
              return (
                <tr key={di} className="border-t border-slate-800/60">
                  <td className="px-1.5 py-0.5 bg-emerald-700/40 text-emerald-200 font-bold sticky left-0 z-10">{dayShortI18n(t, di) || DAY_LABELS_SHORT_FALLBACK[di]}</td>
                  {visibleRouteIds.map((rid) => {
                    const a = dayMap?.get(rid);
                    if (!a) return <td key={rid} className="px-1 py-0.5 bg-slate-900/40">&nbsp;</td>;
                    if (a.status === 'depot') return <td key={rid} className="px-1 py-0.5 bg-slate-800 text-slate-300 font-bold text-center">{t('planning.boardCanvas.depot')}</td>;
                    const initials = (a.employee_name || '').split(' ').slice(0, 2).map((s) => s[0]).join('').toUpperCase();
                    const plate = (a.vehicle_code || '').trim();
                    const tooltip = plate ? `${a.employee_name} · ${plate}` : a.employee_name;
                    return (
                      <td
                        key={rid}
                        className="px-1 py-0.5 bg-blue-600/80 text-white text-center font-bold leading-tight"
                        title={tooltip}
                      >
                        <div>{initials || '·'}</div>
                        {plate && (
                          <div className="text-[8px] font-mono font-semibold text-amber-200/95 truncate">
                            {plate}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!expanded && routeIds.length > ROUTES_PER_FIRM && (
        <div className="px-2 py-0.5 text-[9px] text-slate-500 italic text-right bg-slate-950/40 border-t border-slate-800/60">
          +{routeIds.length - ROUTES_PER_FIRM} rota daha
        </div>
      )}
    </div>
  );
}

